import React, { useState, useRef, useEffect } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Sparkles,
  Loader2,
  Search,
  X,
  ChevronDown,
  Play,
  Users,
  Zap,
  Crown,
  FastForward,
  AlignLeft,
  Mic,
  FileText,
  ImagePlus,
  Brain,
  Check,
  SlidersHorizontal,
} from "lucide-react";
import imageCompression from "browser-image-compression";
import {
  loadDebateDraft,
  saveDebateDraft,
  saveDebatePrefs,
  clearDebateDraft,
} from "../utils/debatePrefs";
import {
  ExpertOption,
  ExpertCategory,
  MAX_EXPERTS,
  getOrderedCategories,
  getExpertsByCategory,
} from "../constants/experts";
import { getExpertPalette } from "../utils/expertStyles";
import StepTab from "./StepTab";
import SelectionDot, { useDustTransfer } from "./SelectionDot";
import {
  DebateCredits,
  WeeklyUsage,
  WEEKLY_FREE_DEBATE_LIMIT,
  GUEST_WEEKLY_FREE_DEBATE_LIMIT,
  WEEKLY_AUDIO_DEBATE_LIMIT,
} from "../firebase/credits";
import Switch from "./Switch";
import AnswerFormatDialog from "./AnswerFormatDialog";
import { FeedShare } from "../firebase/feed";
import { FEED_ENABLED } from "../utils/features";
import { DebatePrep } from "../firebase/prepare";
import { useDebatePrep } from "../hooks/useDebatePrep";
import { AnswerFormat, formatSummary } from "../utils/verdict";
import { onTierIntelligenceSnapshot, TierIntelligence } from "../firebase/tierIntelligence";
import TierScore from "./TierScore";

/** What the wizard hands over when a debate starts. */
export interface DebateLaunch {
  /** Set when the panel was picked in this same tap, before parent state caught up. */
  experts?: string[];
  share?: FeedShare;
  answerFormat?: AnswerFormat;
  category?: string;
}

interface DebateWizardProps {
  selectedExperts: string[];
  onToggleExpert: (expert: string) => void;
  onSetExperts: (experts: string[]) => void;
  onClearExperts: () => void;
  expertOptions: ExpertOption[];
  onStartDebate: (question: string, images: string[], launch?: DebateLaunch) => void;
  isLoading: boolean;
  hasDebateContent: boolean;
  plan: "free" | "plus" | "pro";
  onSetPlan: (p: "free" | "plus" | "pro") => void;
  concise: boolean;
  onSetConcise: (v: boolean) => void;
  audioMode: boolean;
  onSetAudioMode: (v: boolean) => void;
  credits: DebateCredits;
  isGuest: boolean;
  onSignIn: () => void;
  usage: WeeklyUsage;
  debateMode: "single" | "multi";
  onSetDebateMode: (m: "single" | "multi") => void;
  isPrivileged?: boolean;
}

type WizardStep = number;

function OptionCard({
  selected,
  group,
  value,
  icon,
  title,
  hint,
  badge,
  onClick,
}: {
  selected: boolean;
  group: string;
  value: string;
  icon: React.ReactNode;
  title: string;
  hint: string;
  badge?: string;
  onClick: () => void;
}) {
  return (
    <button type="button" aria-pressed={selected} onClick={onClick} className={`choice-card ${selected ? "is-selected" : ""}`}>
      <span className="flex items-start justify-between gap-2">
        <span className="choice-card-icon">{icon}</span>
        <span className="flex items-center gap-2">
          {badge && <span className="text-xs font-semibold px-1.5 py-0.5 rounded-md border border-line text-muted">{badge}</span>}
          <SelectionDot selected={selected} group={group} value={value} />
        </span>
      </span>
      <span className="block mt-3 text-sm font-semibold text-content">{title}</span>
      <span className="block mt-0.5 text-xs text-muted">{hint}</span>
    </button>
  );
}

const DebateWizard: React.FC<DebateWizardProps> = ({
  selectedExperts,
  onToggleExpert,
  onSetExperts,
  onClearExperts,
  expertOptions,
  onStartDebate,
  isLoading,
  hasDebateContent,
  plan,
  onSetPlan,
  concise,
  onSetConcise,
  audioMode,
  onSetAudioMode,
  credits,
  isGuest,
  onSignIn,
  usage,
  debateMode,
  onSetDebateMode,
  isPrivileged = false,
}) => {
  const [step, setStep] = useState<WizardStep>(1);
  const [tierIntelligence, setTierIntelligence] = useState<TierIntelligence | null>(null);
  useEffect(() => onTierIntelligenceSnapshot(setTierIntelligence), []);
  const TOTAL_STEPS = debateMode === "single" ? 4 : 6;
  const [initialDraft] = useState(loadDebateDraft);
  const [question, setQuestion] = useState(initialDraft.question);
  const topic = question.trim();
  // Experts are picked and the question classified in the background once the
  // user moves past the topic, so the panel is ready by the time they see it.
  const prep = useDebatePrep(question);
  const prepForTopic = prep.result && prep.result.question === topic ? prep.result : null;
  // The topic the panel was last filled in for, and whether the user has
  // changed it since. An edited panel is never replaced automatically.
  const [panelFor, setPanelFor] = useState<string | null>(null);
  const [panelEdited, setPanelEdited] = useState(false);
  // An answer format the user corrected on the review step, for one topic.
  const [formatOverride, setFormatOverride] = useState<{ topic: string; format: AnswerFormat } | null>(null);
  const [formatDialogOpen, setFormatDialogOpen] = useState(false);
  const [launching, setLaunching] = useState(false);
  // Posting is opt-in for every debate, so this never persists between launches.
  const [shareToFeed, setShareToFeed] = useState(false);
  const [shareAnonymously, setShareAnonymously] = useState(false);
  const [launchError, setLaunchError] = useState<string | null>(null);
  const [expertSearch, setExpertSearch] = useState("");
  const [activeCategoryFilter, setActiveCategoryFilter] = useState<string>("all");
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(
    () => new Set<string>(),
  );
  const questionRef = useRef<HTMLTextAreaElement>(null);
  // Counts attempts to move on without a topic, so every attempt replays the flash.
  const [topicNudge, setTopicNudge] = useState(0);
  useEffect(() => {
    const field = questionRef.current;
    if (topicNudge === 0 || step !== 1 || !field) return;
    field.focus();
    if (!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      field.animate?.(
        [{ backgroundColor: "rgb(var(--color-danger-soft))" }, { backgroundColor: "rgb(var(--color-surface))" }],
        { duration: 700, easing: "cubic-bezier(0.2, 0, 0, 1)" },
      );
    }
  }, [topicNudge, step]);
  const searchRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const [imageError, setImageError] = useState("");

  // ── Image upload state ────────────────────────────────────────────────
  const MAX_IMAGES = 2;
  const [images, setImages] = useState<string[]>(initialDraft.images); // base64 data URIs
  const [compressingImages, setCompressingImages] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    await attachImages(Array.from(files));
  };

  const handleQuestionPaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const files = Array.from(e.clipboardData.items)
      .filter((item) => item.kind === "file" && item.type.startsWith("image/"))
      .map((item) => item.getAsFile())
      .filter((f): f is File => f !== null);
    if (files.length === 0) return;
    e.preventDefault();
    void attachImages(files);
  };

  const attachImages = async (files: File[]) => {
    const filesToProcess = files.slice(0, MAX_IMAGES - images.length);
    if (filesToProcess.length === 0) return;

    setImageError("");
    setCompressingImages(true);
    try {
      const compressedDataUris = await Promise.all(
        filesToProcess.map(async (file) => {
          const compressed = await imageCompression(file, {
            maxSizeMB: 0.5,
            maxWidthOrHeight: 1024,
            useWebWorker: true,
            fileType: "image/jpeg",
          });
          return new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(compressed);
          });
        }),
      );
      setImages((prev) =>
        [...prev, ...compressedDataUris].slice(0, MAX_IMAGES),
      );
    } catch (err) {
      console.error("Image compression failed:", err);
      setImageError("That image couldn’t be attached. Try a JPG, PNG, or WebP image.");
    } finally {
      setCompressingImages(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeImage = (idx: number) => {
    setImages((prev) => prev.filter((_, i) => i !== idx));
  };

  const knownExperts = new Set(expertOptions.map((o) => o.value));
  const pickedExperts = (p: DebatePrep) =>
    p.experts.map((e) => e.key).filter((k) => knownExperts.has(k)).slice(0, MAX_EXPERTS);

  // Fill the panel from the background pick unless the user has made it their own.
  const applyPrep = (p: DebatePrep): string[] | null => {
    const experts = pickedExperts(p);
    if (experts.length < 2) return null;
    onSetExperts(experts);
    setPanelFor(p.question);
    setPanelEdited(false);
    return experts;
  };

  const { prepare } = prep;
  useEffect(() => {
    if (step >= 2 && topic && debateMode === "multi") prepare(topic);
  }, [step, topic, debateMode, prepare]);

  useEffect(() => {
    if (debateMode !== "multi" || !prepForTopic || panelEdited || panelFor === topic) return;
    applyPrep(prepForTopic);
    // applyPrep only depends on values listed here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prepForTopic, debateMode, panelEdited, panelFor, topic]);

  // The panel hasn't been filled in for this topic and the user hasn't built one.
  const autoPending = debateMode === "multi" && !panelEdited && panelFor !== topic;
  // What the expert step shows as the panel; a panel picked for an earlier topic isn't one.
  const panel = autoPending ? [] : selectedExperts;

  const toggleExpert = (value: string) => {
    setPanelEdited(true);
    if (autoPending) {
      // Start the user's own panel from scratch rather than on top of a stale one.
      onSetExperts([value]);
      setPanelFor(topic);
      return;
    }
    onToggleExpert(value);
  };

  const detectedFormat = prepForTopic?.format ?? null;
  const answerFormat =
    formatOverride && formatOverride.topic === topic ? formatOverride.format : detectedFormat;

  useDustTransfer("tier", plan);
  useDustTransfer("mode", debateMode);
  useDustTransfer("format", audioMode ? "audio" : "text");
  useDustTransfer("depth", concise ? "concise" : "verbose");

  const prevStepRef = useRef(step);
  const stepDirection = step < prevStepRef.current ? "back" : "forward";
  useEffect(() => { prevStepRef.current = step; }, [step]);

  // Panels are picked per topic, so only the setup choices are remembered.
  useEffect(() => {
    saveDebatePrefs({ plan, concise, audioMode, debateMode, autoExperts: true, experts: [] });
  }, [plan, concise, audioMode, debateMode]);

  useEffect(() => {
    if (hasDebateContent) return;
    const timer = setTimeout(() => saveDebateDraft({ question, images }), 400);
    return () => clearTimeout(timer);
  }, [question, images, hasDebateContent]);

  // Remembered choices must never leave someone on an option they can't use.
  useEffect(() => {
    if (isGuest) {
      if (plan !== "free") onSetPlan("free");
      if (audioMode) onSetAudioMode(false);
      return;
    }
    if (!isPrivileged && credits.initialized) {
      if (plan === "plus" && credits.plusCredits <= 0) onSetPlan("free");
      if (plan === "pro" && credits.proCredits <= 0) onSetPlan("free");
    }
  }, [isGuest, isPrivileged, plan, audioMode, credits, onSetPlan, onSetAudioMode]);

  // Announce each new screen without opening the phone keyboard or retaining scroll.
  useEffect(() => {
    canvasRef.current?.scrollTo?.({ top: 0 });
    const heading = canvasRef.current?.querySelector<HTMLElement>("h1");
    if (heading && step > 1) {
      heading.tabIndex = -1;
      heading.focus({ preventScroll: true });
    }
  }, [step]);

  // Reset wizard when returning from completed debate
  const prevHasDebateContent = useRef(hasDebateContent);
  useEffect(() => {
    if (prevHasDebateContent.current && !hasDebateContent) {
      setStep(1);
      setQuestion("");
      setExpertSearch("");
      setImages([]);
      setPanelFor(null);
      setPanelEdited(false);
      setFormatOverride(null);
      setLaunchError(null);
      clearDebateDraft();
    }
    prevHasDebateContent.current = hasDebateContent;
  }, [hasDebateContent]);

  if (isLoading || hasDebateContent) return null;

  // ── Expert picker helpers ─────────────────────────────────────────────
  const orderedCategories = getOrderedCategories();
  const expertsByCategory = getExpertsByCategory();

  const toggleCategory = (cat: string) =>
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      next.has(cat) ? next.delete(cat) : next.add(cat);
      return next;
    });

  const filteredExpertsByCategory: Record<ExpertCategory, ExpertOption[]> =
    (() => {
      const q = expertSearch.toLowerCase().trim();
      const result = {} as Record<ExpertCategory, ExpertOption[]>;
      for (const cat of orderedCategories) {
        if (activeCategoryFilter !== "all" && activeCategoryFilter !== cat) {
          continue;
        }
        const experts = expertsByCategory[cat] || [];
        if (!q) {
          result[cat] = experts;
        } else {
          const filtered = experts.filter(
            (e) =>
              e.label.toLowerCase().includes(q) ||
              e.description.toLowerCase().includes(q) ||
              cat.toLowerCase().includes(q),
          );
          if (filtered.length > 0) result[cat] = filtered;
        }
      }
      return result;
    })();

  const filteredCategories = orderedCategories.filter(
    (cat) => filteredExpertsByCategory[cat]?.length > 0,
  );

  const selectedCountByCategory: Record<string, number> = (() => {
    const counts: Record<string, number> = {};
    for (const opt of expertOptions) {
      if (panel.includes(opt.value)) {
        counts[opt.category] = (counts[opt.category] || 0) + 1;
      }
    }
    return counts;
  })();

  // ── Step indicator and labels ─────────────────────────────────────────
  const stepLabels =
    debateMode === "single"
      ? ["Topic & Context", "Knowledge Level", "Debate Mode", "Review & Launch"]
      : [
          "Topic & Context",
          "Knowledge Level",
          "Debate Mode",
          "Council Experts",
          "Format & Style",
          "Review & Launch",
        ];

  // Limit and usage calculations
  const freeLimit = isGuest
    ? GUEST_WEEKLY_FREE_DEBATE_LIMIT
    : WEEKLY_FREE_DEBATE_LIMIT;
  const freeLimitReached = !isPrivileged && usage.freeDebates >= freeLimit;
  const freeRemaining = Math.max(0, freeLimit - usage.freeDebates);
  const freePct = Math.min(100, (freeRemaining / freeLimit) * 100);
  const showAudio = !isGuest && audioMode && debateMode === "multi";
  const audioRemaining = Math.max(
    0,
    WEEKLY_AUDIO_DEBATE_LIMIT - usage.audioDebates,
  );
  // A pending automatic panel counts as ready: launch fills it in (retrying a failed pick).
  const panelReady = debateMode === "single" || autoPending || selectedExperts.length >= 2;
  const setupValid = question.trim().length > 0 && panelReady;
  const needsTopic = question.trim().length === 0;
  const requireTopic = () => {
    setTopicNudge((n) => n + 1);
    setStep(1);
  };
  const showTopicError = topicNudge > 0 && needsTopic;
  const canVisitStep = (target: number) =>
    target === TOTAL_STEPS ? setupValid : target <= 4 || panelReady;

  const limitReached =
    !isPrivileged && plan === "free" && usage.freeDebates >= freeLimit;
  const audioLimitReached =
    !isPrivileged &&
    plan === "free" &&
    audioMode &&
    !isGuest &&
    debateMode === "multi" &&
    usage.audioDebates >= WEEKLY_AUDIO_DEBATE_LIMIT;
  const isBlocked = limitReached || audioLimitReached;

  const launchDebate = async () => {
    if (needsTopic) {
      requireTopic();
      return;
    }
    if (!setupValid || isBlocked || compressingImages || launching) return;
    setLaunchError(null);
    let experts: string[] | undefined;
    let ready = prepForTopic;
    // Multi-expert debates wait for the background prep: it fills an automatic
    // panel and supplies the answer format. A failed prep only blocks an
    // automatic panel; the server classifies the question itself otherwise.
    if (debateMode === "multi" && !ready) {
      setLaunching(true);
      ready = await prep.ensure(topic);
      setLaunching(false);
    }
    if (autoPending) {
      const picked = ready ? applyPrep(ready) : null;
      if (!picked) {
        setLaunchError(
          "Couldn’t pick a panel automatically. Try again, or choose the experts yourself.",
        );
        return;
      }
      // Audio playback has to begin inside a fresh tap, so show the panel first.
      if (audioMode) {
        setStep(TOTAL_STEPS);
        return;
      }
      experts = picked;
    }
    const format =
      formatOverride && formatOverride.topic === topic ? formatOverride.format : ready?.format;
    clearDebateDraft();
    const launch: DebateLaunch = {};
    if (experts) launch.experts = experts;
    if (shareToFeed && !isGuest) launch.share = { anonymous: shareAnonymously };
    if (debateMode === "multi" && format) launch.answerFormat = format;
    if (debateMode === "multi" && ready?.category) launch.category = ready.category;
    onStartDebate(question, images, launch);
  };

  // ── Navigation config ────────────────────────────────────────────────
  const getNavConfig = (): {
    onBack?: () => void;
    secondary?: { label: string; onClick: () => void };
    onNext: () => void;
    nextLabel: string;
    nextDisabled: boolean;
    nextIcon?: React.ReactNode;
  } => {
    const busy = launching;
    const launchNav = {
      onNext: () => void launchDebate(),
      nextLabel: isBlocked
        ? "Weekly limit reached"
        : busy
          ? autoPending
            ? "Picking experts…"
            : "Starting…"
          : debateMode === "single"
            ? "Launch analysis"
            : "Start debate",
      nextDisabled: isBlocked || !panelReady || compressingImages || busy,
      nextIcon: isBlocked ? undefined : busy ? (
        <Loader2 size={16} className="animate-spin" aria-hidden="true" />
      ) : (
        <Play size={16} />
      ),
    };
    const addTopic = {
      onNext: requireTopic,
      nextLabel: "Add a topic to continue",
      nextDisabled: false,
    };

    if (step === 1) {
      return {
        ...launchNav,
        secondary: { label: "Customize setup", onClick: () => (needsTopic ? requireTopic() : setStep(2)) },
      };
    }
    if (step === 2) {
      return {
        onBack: () => setStep(1),
        onNext: () => setStep(3),
        nextLabel: "Continue to Mode",
        nextDisabled: false,
      };
    }
    if (step === 3) {
      if (debateMode === "single") {
        return needsTopic
          ? { onBack: () => setStep(2), ...addTopic }
          : {
              onBack: () => setStep(2),
              onNext: () => setStep(4),
              nextLabel: "Review analysis",
              nextDisabled: false,
            };
      }
      return {
        onBack: () => setStep(2),
        onNext: () => setStep(4),
        nextLabel: "Choose experts",
        nextDisabled: false,
      };
    }
    if (debateMode === "single") {
      return { onBack: () => setStep(3), ...launchNav };
    }
    if (step === 4) {
      return {
        onBack: () => setStep(3),
        onNext: () => setStep(5),
        nextLabel: "Set format & style",
        // Moving on while the pick is still running is fine; after a failed pick the user chooses.
        nextDisabled: autoPending ? prep.status === "error" : selectedExperts.length < 2,
      };
    }
    if (step === 5) {
      return needsTopic
        ? { onBack: () => setStep(4), ...addTopic }
        : {
            onBack: () => setStep(4),
            onNext: () => setStep(6),
            nextLabel: "Review setup",
            nextDisabled: false,
          };
    }
    return { onBack: () => setStep(5), ...launchNav };
  };

  const navConfig = getNavConfig();

  // ── Step content ─────────────────────────────────────────────────────
  let stepContent: React.ReactNode = null;

  // ── Step 2: Knowledge level ───────────────────────────────────────────
  if (step === 2) {
    const plans: {
      value: "free" | "plus" | "pro";
      label: string;
      icon: React.ReactNode;
      color: string;
      ring: string;
      desc: string;
      features: string[];
      badge?: string;
    }[] = [
      {
        value: "free",
        label: "Free",
        icon: <Zap size={24} className="text-accent" />,
        color: "border-accent-line bg-surface",
        ring: "ring-accent-line",
        desc: "Capable multi-expert debate using fast, open-access models.",
        features: [
          "Standard reasoning models",
          "Full panel cross-examination",
          `${freeRemaining}/${freeLimit} debates available this week`,
        ],
        badge: freeLimitReached
          ? "Limit reached"
          : `${freeRemaining}/${freeLimit} left`,
      },
      {
        value: "plus",
        label: "Plus",
        icon: <Zap size={24} className="text-accent" />,
        color: "border-accent-line bg-surface",
        ring: "ring-accent-line",
        desc: "Enhanced reasoning with deeper critical analysis and factual accuracy.",
        features: [
          "High-performance reasoning models",
          "Advanced fact-checking depth",
          isPrivileged
            ? "Free access on your account"
            : `${credits.plusCredits} credits available`,
        ],
        badge: isPrivileged ? "Free access" : `${credits.plusCredits} left`,
      },
      {
        value: "pro",
        label: "Pro",
        icon: <Crown size={24} className="text-accent" />,
        color: "border-accent-line bg-surface",
        ring: "ring-accent-line",
        desc: "Frontier flagship models. Highest intelligence, nuance, and complexity.",
        features: [
          "Flagship frontier AI models",
          "Maximum nuance and philosophical rigor",
          isPrivileged
            ? "Free access on your account"
            : `${credits.proCredits} credits available`,
        ],
        badge: isPrivileged ? "Free access" : `${credits.proCredits} left`,
      },
    ];

    stepContent = (
      <div className="w-full max-w-6xl space-y-6">
        <div>
          <h1 className="text-2xl sm:text-2xl font-semibold text-content leading-tight">
            Choose knowledge tier
          </h1>
          <p className="text-sm sm:text-base text-muted mt-1">
            Select the model intelligence level powering your expert council.
          </p>
        </div>

        <div className="tier-grid">
          {plans.map((p) => {
            const isSelected = plan === p.value;
            const locked = isGuest && (p.value === "plus" || p.value === "pro");
            const freeBlocked = p.value === "free" && freeLimitReached;
            const intelligence = tierIntelligence?.tiers[p.value];
            return (
              <div
                key={p.value}
                className={`tier-card ${isSelected ? "is-selected" : ""} ${freeBlocked ? "is-blocked" : ""} w-full text-left flex flex-col justify-between p-6 rounded-lg border-2 transition-all ${
                  isSelected && !freeBlocked
                    ? "border-accent-line bg-surface ring-2 ring-accent-line/20 shadow-sm"
                    : freeBlocked
                      ? "bg-subtle border-line"
                      : "bg-surface border-line hover:border-accent-line/60 hover:shadow-xs"
                }`}
              >
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div
                      className={`p-3 rounded-xl ${
                        isSelected && !freeBlocked
                          ? "bg-accent-soft"
                          : "bg-raised"
                      }`}
                    >
                      {p.icon}
                    </div>
                    <span className="flex items-center gap-2.5">
                      {p.badge && (
                        <span
                          className={`text-xs font-semibold px-2.5 py-0.5 rounded-md border ${
                            freeBlocked
                              ? "bg-danger-soft border-danger-line text-danger"
                              : "bg-surface border-line text-muted"
                          }`}
                        >
                          {locked ? "Sign in" : p.badge}
                        </span>
                      )}
                      <SelectionDot selected={isSelected && !freeBlocked} group="tier" value={p.value} />
                    </span>
                  </div>
                  <header className="tier-name-row">
                    <h2
                      className={`text-xl font-semibold ${
                        freeBlocked ? "text-muted" : "text-content"
                      }`}
                    >
                      <button
                        type="button"
                        className="tier-card-select"
                        aria-pressed={isSelected && !freeBlocked}
                        aria-label={locked ? `${p.label}, sign in to choose` : undefined}
                        aria-describedby={`tier-desc-${p.value}`}
                        disabled={freeBlocked}
                        onClick={() => {
                          if (locked) {
                            onSignIn();
                            return;
                          }
                          onSetPlan(p.value);
                        }}
                      >
                        {p.label}
                      </button>
                    </h2>
                    {intelligence && tierIntelligence && (
                      <TierScore
                        tierLabel={p.label}
                        score={intelligence.score}
                        modelCount={intelligence.modelCount}
                        updatedAt={tierIntelligence.generatedAt}
                      />
                    )}
                  </header>
                  <p
                    id={`tier-desc-${p.value}`}
                    className={`text-xs mt-1.5 leading-relaxed min-h-[38px] ${
                      freeBlocked ? "text-muted" : "text-muted"
                    }`}
                  >
                    {freeBlocked
                      ? "Weekly limit reached — resets Monday"
                      : p.desc}
                  </p>
                  <ul className="mt-5 space-y-2 pt-4 border-t border-line">
                    {p.features.map((feat, i) => (
                      <li
                        key={i}
                        className="flex items-center gap-2 text-xs text-muted"
                      >
                        <Check size={14} className="text-accent flex-shrink-0" />
                        <span>{feat}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>

        {freeLimitReached && plan === "free" && (
          <div className="rounded-xl bg-danger-soft border border-danger-line p-4 text-center">
            <p className="text-sm font-semibold text-danger">
              You've used all {freeLimit} free debates this week
            </p>
            <p className="text-xs text-danger mt-0.5">
              Upgrade to Plus or Pro to keep debating, or come back Monday.
            </p>
          </div>
        )}
      </div>
    );
  }

  // ── Step 3: Debate mode ───────────────────────────────────────────────
  if (step === 3) {
    const modes: {
      value: "multi" | "single";
      label: string;
      icon: React.ReactNode;
      color: string;
      ring: string;
      desc: string;
      highlights: string[];
    }[] = [
      {
        value: "multi",
        label: "Multi-Expert Debate",
        icon: <Users size={26} className="text-accent" />,
        color: "border-accent-line bg-surface",
        ring: "ring-accent-line",
        desc: "A roundtable council of domain experts challenging each other from competing viewpoints.",
        highlights: [
          "Interactive cross-examination between specialized experts",
          "Fact-checkers challenge biases and false premises",
          "Synthesized consensus with actionable takeaways",
          "Customizable panel or AI auto-curation",
        ],
      },
      {
        value: "single",
        label: "Deep Analysis",
        icon: <Brain size={26} className="text-accent" />,
        color: "border-accent-line bg-surface",
        ring: "ring-focus",
        desc: "A singular analytical lead conducting exhaustive research, framing, and structured evaluation.",
        highlights: [
          "Exhaustive domain deep dive on your question",
          "Multi-stage web research and verification pipeline",
          "Comprehensive executive summary and factual report",
          "Faster, highly focused single stream",
        ],
      },
    ];

    stepContent = (
      <div className="w-full max-w-5xl space-y-6">
        <div>
          <h1 className="text-2xl sm:text-2xl font-semibold text-content leading-tight">
            Choose debate mode
          </h1>
          <p className="text-sm sm:text-base text-muted mt-1">
            Choose whether to pit multiple specialists against each other or run a focused deep dive.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {modes.map((m) => {
            const isSelected = debateMode === m.value;
            return (
              <button
                key={m.value}
                type="button"
                aria-pressed={isSelected}
                onClick={() => onSetDebateMode(m.value)}
                className={`w-full text-left flex flex-col justify-between p-6 sm:p-7 rounded-lg border-2 transition-all ${
                  isSelected
                    ? "border-accent-line bg-surface ring-2 ring-accent-line/20 shadow-sm"
                    : "bg-surface border-line hover:border-accent-line/60 hover:shadow-xs"
                }`}
              >
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div
                      className={`p-3.5 rounded-xl ${
                        isSelected ? "bg-surface shadow-xs" : "bg-raised"
                      }`}
                    >
                      {m.icon}
                    </div>
                    <SelectionDot selected={isSelected} group="mode" value={m.value} />
                  </div>
                  <h2 className="text-xl font-semibold text-content">{m.label}</h2>
                  <p className="text-xs text-muted mt-1.5 leading-relaxed">
                    {m.desc}
                  </p>
                  <ul className="mt-5 space-y-2.5 pt-4 border-t border-line">
                    {m.highlights.map((h, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-2.5 text-xs text-muted"
                      >
                        <Check size={15} className="text-accent flex-shrink-0 mt-0.5" />
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // ── Step 1: Question / Topic ──────────────────────────────────────────
  const launchErrorNotice = launchError && (
    <p role="alert" className="motion-fade text-sm text-danger">
      {launchError}{" "}
      {debateMode === "multi" && (
        <button
          type="button"
          onClick={() => {
            setLaunchError(null);
            setStep(4);
          }}
          className="font-semibold underline underline-offset-2 hover:text-danger rounded-sm focus-visible:outline-offset-2"
        >
          Choose experts
        </button>
      )}
    </p>
  );

  if (step === 1) {
    const starterCategories = [
      {
        tag: "Tech & AI",
        topics: [
          "Should autonomous AI coding agents require formal engineering licensing?",
          "Open source frontier AI models vs restricted API-only access",
        ],
      },
      {
        tag: "Finance & Wealth",
        topics: [
          "Passive total-market index funds vs active value stock picking",
          "Real estate property ownership vs equities for generational wealth",
        ],
      },
      {
        tag: "Society & Policy",
        topics: [
          "Is nuclear energy strictly required to reach global net-zero carbon?",
          "Permanent remote work policies enhance vs diminish collaborative innovation",
        ],
      },
    ];

    const setupSummary: { label: string; value: string; step: WizardStep }[] = [
      { label: "Tier", value: plan.charAt(0).toUpperCase() + plan.slice(1), step: 2 },
      { label: "Mode", value: debateMode === "multi" ? "Multi-expert" : "Deep analysis", step: 3 },
      ...(debateMode === "multi"
        ? [
            {
              label: "Panel",
              value: autoPending ? "Picked for your topic" : `${selectedExperts.length} experts`,
              step: 4 as WizardStep,
            },
            {
              label: "Format",
              value: `${audioMode ? "Voice" : "Text"} · ${concise ? "Concise" : "Verbose"}`,
              step: 5 as WizardStep,
            },
          ]
        : []),
    ];

    stepContent = (
      <div className="w-full max-w-6xl space-y-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-content leading-tight">
            What would you like to {debateMode === "single" ? "analyze" : "debate"}?
          </h1>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 lg:gap-8 items-start">
          <div className="xl:col-span-7 space-y-5">
            <div>
              <label
                htmlFor="debate-question-input"
                className="sr-only"
              >
                Debate Prompt
              </label>
              <textarea
                id="debate-question-input"
                ref={questionRef}
                value={question}
                onChange={(e) => {
                  setQuestion(e.target.value);
                  setLaunchError(null);
                  setTopicNudge(0);
                }}
                onPaste={handleQuestionPaste}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.ctrlKey || e.metaKey) && !e.nativeEvent.isComposing) {
                    e.preventDefault();
                    void launchDebate();
                  }
                }}
                placeholder="e.g. Should our engineering team migrate from microservices to a modular monolith?"
                aria-invalid={showTopicError || undefined}
                aria-describedby={showTopicError ? "debate-question-error" : undefined}
                className={`w-full border bg-surface rounded-lg p-4 resize-none focus:outline-none focus-visible:ring-2 text-base transition-colors duration-150 ${
                  showTopicError
                    ? "!border-danger-line focus-visible:ring-danger-line/30"
                    : "border-line hover:border-control focus-visible:ring-accent-line/25 focus:border-accent-line"
                }`}
                rows={5}
              />
              {showTopicError && (
                <p id="debate-question-error" role="alert" className="motion-fade mt-2 text-sm text-danger">
                  Enter a {debateMode === "single" ? "question" : "topic"} first.
                </p>
              )}

              <div className="mt-3 flex items-center flex-wrap gap-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={handleImageSelect}
                />

                <div className="flex items-center gap-2 flex-wrap">
                  {images.map((dataUri, idx) => (
                    <div key={idx} className="relative group">
                      <img
                        src={dataUri}
                        alt={`Attachment ${idx + 1}`}
                        className="w-16 h-16 object-cover rounded-md border border-line"
                      />
                      <button
                        type="button"
                        aria-label={`Remove attachment ${idx + 1}`}
                        onClick={() => removeImage(idx)}
                        className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-media text-on-solid rounded-full flex items-center justify-center hover:bg-media focus-visible:outline-offset-2"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}

                  <button
                    type="button"
                    disabled={images.length >= MAX_IMAGES || compressingImages}
                    onClick={() => fileInputRef.current?.click()}
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-md border border-line bg-surface text-content hover:bg-subtle active:bg-raised focus-visible:outline-offset-2 disabled:text-muted disabled:bg-subtle disabled:cursor-not-allowed"
                  >
                    {compressingImages ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <ImagePlus size={14} />
                    )}
                    <span>
                      {compressingImages
                        ? "Compressing…"
                        : images.length > 0
                          ? `Add photo (${images.length}/${MAX_IMAGES})`
                          : "Attach image"}
                    </span>
                  </button>
                </div>
              </div>
              {imageError && <p role="alert" className="mt-3 text-sm text-danger">{imageError}</p>}
            </div>

            <div className="border-t border-line pt-4">
              <h2 className="text-sm font-medium text-muted">Your setup</h2>
              <div className="mt-2 flex flex-wrap gap-2">
                {setupSummary.map((item) => (
                  <button
                    key={item.label}
                    type="button"
                    onClick={() => setStep(item.step)}
                    aria-label={`Change ${item.label.toLowerCase()}: ${item.value}`}
                    className="inline-flex items-center gap-1.5 rounded-md border border-line bg-surface px-2.5 py-1.5 text-xs text-content hover:bg-subtle hover:border-control active:bg-raised focus-visible:outline-offset-2 transition-colors duration-150"
                  >
                    <span className="text-muted">{item.label}</span>
                    <span className="font-medium">{item.value}</span>
                  </button>
                ))}
              </div>
            </div>

            {launchErrorNotice}
          </div>

          <div className="xl:col-span-5 xl:border-l xl:border-line xl:pl-8 space-y-4">
            <h2 className="text-sm font-medium text-content">Topic starters</h2>
            <div className="space-y-4">
              {starterCategories.map((group, gIdx) => (
                <div key={gIdx} className="space-y-1.5">
                  <span className="text-xs font-medium text-muted">
                    {group.tag}
                  </span>
                  {group.topics.map((t, tIdx) => (
                    <button
                      key={tIdx}
                      type="button"
                      onClick={() => {
                        setQuestion(t);
                        setTimeout(() => questionRef.current?.focus(), 50);
                      }}
                      className="w-full text-left text-xs bg-surface hover:bg-subtle active:bg-raised border border-line hover:border-control rounded-md p-2.5 text-content transition-colors duration-150 leading-snug focus-visible:outline-offset-2"
                    >
                      {t}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Step 4: Choose Experts (Multi mode) ────────────────────────────────
  if (step === 4 && debateMode === "multi") {
    const reasons = new Map(prepForTopic?.experts.map((e) => [e.key, e.reason]) ?? []);
    const suggested = prepForTopic ? pickedExperts(prepForTopic) : [];
    // Offer the topic's pick when the user's own panel differs from it.
    const offerSuggested =
      !autoPending &&
      suggested.length >= 2 &&
      (panelFor !== topic || suggested.join() !== [...selectedExperts].join());
    const picking = autoPending && (prep.status === "loading" || prep.status === "idle") && !needsTopic;
    const pickFailed = autoPending && prep.status === "error";

    stepContent = (
      <div className="w-full space-y-6">
        <div>
          <h1 className="text-2xl sm:text-2xl font-semibold text-content leading-tight">
            Assemble your council
          </h1>
          <p className="text-sm sm:text-base text-muted mt-1">
            Experts are picked for your topic, with what each one adds. Add or remove any; a panel needs 2 to {MAX_EXPERTS}.
          </p>
        </div>

        <section aria-labelledby="panel-heading" className="max-w-3xl">
          <div className="flex items-baseline justify-between gap-3 border-b border-line pb-2">
            <h2 id="panel-heading" className="text-sm font-medium text-content">Your panel</h2>
            <span className="text-xs text-muted" aria-live="polite">
              <span className="font-mono tabular-nums">{panel.length}</span> of{" "}
              <span className="font-mono tabular-nums">{MAX_EXPERTS}</span>
            </span>
          </div>

          {needsTopic ? (
            <p className="py-4 text-sm text-muted">
              Add a topic first and experts are picked for it.{" "}
              <button type="button" onClick={() => setStep(1)} className="font-semibold text-content underline underline-offset-2 rounded-sm hover:text-accent focus-visible:outline-offset-2">
                Go to topic
              </button>
            </p>
          ) : picking ? (
            <div role="status" aria-label="Picking experts for your topic">
              {[0, 1, 2].map((i) => (
                <div key={i} className="flex items-start gap-3 py-3 border-b border-line" aria-hidden="true">
                  <span className="skeleton block h-7 w-7 rounded-md" />
                  <span className="flex-1 space-y-2 pt-0.5">
                    <span className="skeleton block h-3 w-32" />
                    <span className="skeleton block h-3 w-3/4" />
                  </span>
                </div>
              ))}
              <p className="pt-2 text-xs text-muted">Picking experts for your topic…</p>
            </div>
          ) : pickFailed ? (
            <p role="alert" className="py-4 text-sm text-danger">
              Couldn’t pick experts for this topic. Choose 2 to {MAX_EXPERTS} below, or{" "}
              <button type="button" onClick={() => prep.retry(topic)} className="font-semibold underline underline-offset-2 rounded-sm hover:text-danger focus-visible:outline-offset-2">
                try again
              </button>
              .
            </p>
          ) : panel.length === 0 ? (
            <p className="py-4 text-sm text-muted">No experts yet. Choose 2 to {MAX_EXPERTS} below.</p>
          ) : (
            <ul>
              {panel.map((value) => {
                const expert = expertOptions.find((e) => e.value === value);
                if (!expert) return null;
                const palette = getExpertPalette(value, panel);
                const reason = reasons.get(value);
                return (
                  <li key={value} className="motion-fade flex items-start gap-3 py-3 border-b border-line">
                    <span
                      aria-hidden="true"
                      className={`w-7 h-7 rounded-md flex items-center justify-center font-semibold text-xs flex-shrink-0 border ${palette.badgeBg} ${palette.badgeText} ${palette.border}`}
                    >
                      {expert.label.charAt(0).toUpperCase()}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-content">{expert.label}</span>
                      <span className="block text-sm text-muted leading-snug">
                        {reason || (panelFor === topic && reasons.size > 0 ? "Added by you" : expert.description)}
                      </span>
                    </span>
                    <button
                      type="button"
                      onClick={() => toggleExpert(value)}
                      aria-label={`Remove ${expert.label}`}
                      className="flex-shrink-0 h-8 w-8 inline-flex items-center justify-center rounded-md text-muted hover:bg-subtle hover:text-content active:bg-raised focus-visible:outline-offset-2 transition-colors duration-150"
                    >
                      <X size={15} aria-hidden="true" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {!needsTopic && !picking && !pickFailed && panel.length > 0 && panel.length < 2 && (
            <p className="pt-2 text-xs text-warning">Add {2 - panel.length} more to continue.</p>
          )}

          {offerSuggested && (
            <div className="mt-3 flex items-center justify-between gap-3 flex-wrap text-sm">
              <p className="text-muted min-w-0">
                {panelFor !== topic ? "Your topic changed since you chose this panel." : "Suggested for this topic:"}{" "}
                <span className="text-content">
                  {suggested.map((k) => expertOptions.find((e) => e.value === k)?.label ?? k).join(", ")}
                </span>
              </p>
              <button type="button" onClick={() => applyPrep(prepForTopic!)} className="button-secondary !min-h-0 !py-1.5 !px-3">
                Use suggested panel
              </button>
            </div>
          )}
        </section>

        <h2 className="text-sm font-medium text-content">All experts</h2>
        {/* Filter Bar: Search + Category Pills */}
        <div className="bg-surface border border-line rounded-lg p-4 shadow-2xs space-y-3">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <div className="relative flex-1 sm:max-w-md">
              <Search
                size={16}
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted"
              />
              <input
                ref={searchRef}
                type="text"
                placeholder="Search experts by name, role, or description…"
                value={expertSearch}
                onChange={(e) => setExpertSearch(e.target.value)}
                className="w-full text-sm border border-line rounded-xl pl-10 pr-10 py-2.5 bg-subtle focus:bg-surface focus:ring-2 focus:ring-accent-line/20 focus:border-accent-line text-content"
              />
              {expertSearch && (
                <button
                  type="button"
                  aria-label="Clear expert search"
                  onClick={() => setExpertSearch("")}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted hover:text-muted"
                >
                  <X size={16} />
                </button>
              )}
            </div>

            {panel.length >= MAX_EXPERTS && (
              <p className="text-xs text-muted ml-auto" aria-live="polite">Panel full. Remove one to swap.</p>
            )}
          </div>

          {/* Category Filter Pills */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none">
            <button
              type="button"
              onClick={() => setActiveCategoryFilter("all")}
              className={`px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-colors ${
                activeCategoryFilter === "all"
                  ? "bg-action text-on-solid shadow-2xs"
                  : "bg-raised text-muted hover:bg-pressed"
              }`}
            >
              All Categories
            </button>
            {orderedCategories.map((cat) => {
              const isSelected = activeCategoryFilter === cat;
              const count = selectedCountByCategory[cat] || 0;
              return (
                <button
                  key={cat}
                  type="button"
                  onClick={() =>
                    setActiveCategoryFilter(isSelected ? "all" : cat)
                  }
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-colors ${
                    isSelected
                      ? "bg-action text-on-solid shadow-2xs"
                      : "bg-raised text-muted hover:bg-pressed"
                  }`}
                >
                  <span>{cat}</span>
                  {count > 0 && (
                    <span
                      className={`text-xs px-1.5 py-0.2 rounded-md font-semibold ${
                        isSelected
                          ? "bg-surface text-accent"
                          : "bg-action text-on-solid"
                      }`}
                    >
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Full-Width Expert Grid across screen */}
        <div className="space-y-5">
          {filteredCategories.map((cat) => {
            const experts = filteredExpertsByCategory[cat];
            const isCollapsed = collapsedCategories.has(cat) && !expertSearch;
            const catCount = selectedCountByCategory[cat] || 0;
            return (
              <div
                key={cat}
                className="rounded-lg border shadow-2xs border-line bg-surface overflow-hidden"
              >
                <button
                  type="button"
                  aria-expanded={!isCollapsed}
                  className="w-full flex items-center justify-between px-5 py-3.5 bg-subtle/90 hover:bg-raised active:bg-pressed/70 transition-colors duration-150"
                  onClick={() => toggleCategory(cat)}
                >
                  <div className="flex items-center gap-2.5 text-sm font-semibold text-content">
                    <span>{cat}</span>
                    {catCount > 0 && (
                      <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-semibold text-on-solid bg-action rounded-md">
                        {catCount} selected
                      </span>
                    )}
                  </div>
                  <ChevronDown
                    size={16}
                    aria-hidden="true"
                    className={`text-muted transition-transform duration-150 ease-out ${
                      isCollapsed ? "-rotate-90" : ""
                    }`}
                  />
                </button>
                {!isCollapsed && (
                  <div className="motion-fade p-4 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3">
                    {experts.map((expert) => {
                      const isSelected = panel.includes(expert.value);
                      const atCap =
                        panel.length >= MAX_EXPERTS && !isSelected;
                      const palette = getExpertPalette(expert.value, panel);
                      return (
                        <button
                          key={expert.value}
                          type="button"
                          aria-pressed={isSelected}
                          disabled={atCap}
                          onClick={() => toggleExpert(expert.value)}
                          title={expert.description}
                          className={`text-left rounded-xl border-2 p-3 transition-[background-color,border-color,box-shadow,opacity] duration-150 ease-out flex flex-col justify-between focus-visible:outline-offset-2 ${
                            isSelected
                              ? `${palette.headerBg} ${palette.accentText} ${palette.border} shadow-2xs hover:brightness-95 active:brightness-90`
                              : atCap
                                ? "opacity-40 cursor-not-allowed bg-subtle text-muted border-line"
                                : "bg-surface text-content border-line hover:bg-subtle/80 hover:border-control active:bg-raised"
                          }`}
                        >
                          <div>
                            <div className="flex items-center justify-between gap-2 mb-2">
                              <div
                                aria-hidden="true"
                                className={`w-8 h-8 rounded-lg flex items-center justify-center font-semibold text-xs flex-shrink-0 border ${palette.badgeBg} ${palette.badgeText} ${palette.border}`}
                              >
                                {expert.label.charAt(0).toUpperCase()}
                              </div>
                              <SelectionDot selected={isSelected} variant="check" />
                            </div>
                            <span className="block text-xs font-semibold leading-tight line-clamp-1">
                              {expert.label}
                            </span>
                            <span className="block text-xs text-muted mt-1 leading-snug line-clamp-2">
                              {expert.description}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ── Step 5: Format & Style ────────────────────────────────────────────
  if (step === 5 && debateMode === "multi") {
    stepContent = (
      <div className="w-full max-w-5xl space-y-6">
        <div>
          <h1 className="text-2xl sm:text-2xl font-semibold text-content leading-tight">
            Configure delivery format
          </h1>
          <p className="text-sm sm:text-base text-muted mt-1">
            Choose whether to read or listen to the debate, and specify response detail.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          <section className="space-y-3" aria-labelledby="format-heading">
            <h2 id="format-heading" className="text-sm font-medium text-muted">Output format</h2>
            <div className="grid grid-cols-2 gap-3">
              <OptionCard selected={!audioMode} group="format" value="text" icon={<FileText size={22} />}
                title="Text only" hint="Read the arguments" onClick={() => onSetAudioMode(false)} />
              <OptionCard selected={audioMode} group="format" value="audio" icon={<Mic size={22} />}
                title="Voice audio" hint="Full audio playback" badge={isGuest ? "Sign in" : undefined}
                onClick={() => { if (isGuest) { onSignIn(); return; } onSetAudioMode(true); }} />
            </div>
          </section>
          <section className="space-y-3" aria-labelledby="depth-heading">
            <h2 id="depth-heading" className="text-sm font-medium text-muted">Response depth</h2>
            <div className="grid grid-cols-2 gap-3">
              <OptionCard selected={concise} group="depth" value="concise" icon={<FastForward size={22} />}
                title="Concise" hint="Fast, punchy points" onClick={() => onSetConcise(true)} />
              <OptionCard selected={!concise} group="depth" value="verbose" icon={<AlignLeft size={22} />}
                title="Verbose" hint="Full depth and citations" onClick={() => onSetConcise(false)} />
            </div>
          </section>
        </div>
      </div>
    );
  }

  // ── Step 6: Review & Launch (or Step 4 for Single) ────────────────────
  const isPreviewStep =
    (debateMode === "single" && step === 4) ||
    (debateMode === "multi" && step === 6);
  if (isPreviewStep) {
    const selectedExpertData = selectedExperts
      .map((v) => expertOptions.find((e) => e.value === v))
      .filter(Boolean) as ExpertOption[];

    stepContent = (
      <div className="w-full max-w-6xl space-y-6">
        <div>
          <h1 className="text-2xl sm:text-2xl font-semibold text-content leading-tight">
            {debateMode === "single" ? "Ready for a deeper look" : "Ready to convene your council"}
          </h1>
          <p className="text-sm sm:text-base text-muted mt-1">
            Review your configuration below and launch when ready.
          </p>
        </div>

        {/* 2-Column Dashboard on Desktop */}
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 lg:gap-8 items-start">
          <div className="xl:col-span-6 space-y-4">
            <section className="review-section bg-subtle" aria-labelledby="review-topic" style={{ viewTransitionName: "debate-topic" } as React.CSSProperties}>
              <div className="review-section-header">
                <h2 id="review-topic">Debate topic</h2>
                <button type="button" className="review-edit" onClick={() => setStep(1)} aria-label="Edit debate topic">Edit</button>
              </div>
              <p className="text-lg font-semibold text-content leading-relaxed">
                "{question}"
              </p>
              {images.length > 0 && (
                <div className="flex gap-2 mt-4">
                  {images.map((dataUri, idx) => (
                    <img
                      key={idx}
                      src={dataUri}
                      alt={`Attachment ${idx + 1}`}
                      className="w-16 h-16 object-cover rounded-md border border-line"
                    />
                  ))}
                </div>
              )}
            </section>

            <section className="review-section" aria-labelledby="review-config">
              <div className="review-section-header">
                <h2 id="review-config">Configuration</h2>
              </div>
              <dl className="review-config-list">
                {([
                  { label: "Knowledge tier", value: `${plan.charAt(0).toUpperCase() + plan.slice(1)}`, step: 2 as WizardStep },
                  { label: "Mode", value: debateMode === "multi" ? "Multi-expert" : "Deep analysis", step: 3 as WizardStep },
                  ...(debateMode === "multi"
                    ? [
                        { label: "Panel", value: autoPending ? "Picked for your topic" : `${selectedExperts.length} experts`, step: 4 as WizardStep },
                        { label: "Format", value: audioMode ? "Voice audio" : "Text only", step: 5 as WizardStep },
                        { label: "Response depth", value: concise ? "Concise" : "Verbose", step: 5 as WizardStep },
                      ]
                    : []),
                ]).map((row) => (
                  <div key={row.label}>
                    <dt>{row.label}</dt>
                    <dd><span>{row.value}</span><button type="button" className="review-edit" onClick={() => setStep(row.step)} aria-label={`Edit ${row.label.toLowerCase()}`}>Edit</button></dd>
                  </div>
                ))}
                {debateMode === "multi" && (
                  <div>
                    <dt>Answer format</dt>
                    <dd>
                      <span>
                        {answerFormat
                          ? formatSummary(answerFormat)
                          : prep.status === "error"
                            ? "Detected when the debate starts"
                            : "Detecting…"}
                      </span>
                      <button type="button" className="review-edit" onClick={() => setFormatDialogOpen(true)} aria-label="Edit answer format">Edit</button>
                    </dd>
                  </div>
                )}
              </dl>
            </section>

            {!isGuest && FEED_ENABLED && (
              <section className="review-section" aria-labelledby="review-feed">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <h2 id="review-feed" className="text-sm font-semibold text-content">Post to the public feed</h2>
                    <p className="text-xs text-muted mt-0.5">
                      When it finishes, anyone can read the question, the panel's replies and the verdict, with your first name. Attached images stay private. You can remove it later.
                    </p>
                  </div>
                  <Switch checked={shareToFeed} onChange={setShareToFeed} label="Post to the public feed" />
                </div>
                {shareToFeed && (
                  <div className="flex items-center justify-between gap-4 mt-3 pt-3 border-t border-raised">
                    <span className="text-sm text-content">Post without my name</span>
                    <Switch checked={shareAnonymously} onChange={setShareAnonymously} label="Post without my name" />
                  </div>
                )}
              </section>
            )}
          </div>

          {/* Right Column: Confirmed Council Lineup */}
          {debateMode === "multi" && (
            <div className="xl:col-span-6 bg-surface border border-line rounded-lg p-5 shadow-2xs space-y-3" style={{ viewTransitionName: "debate-panel" } as React.CSSProperties}>
              <div className="review-section-header pb-3 border-b border-line">
                <h2>
                  Panelists {!autoPending && <span className="font-mono tabular-nums text-muted">{selectedExpertData.length}</span>}
                </h2>
                <button type="button" className="review-edit" onClick={() => setStep(4)} aria-label="Edit panel">Edit</button>
              </div>
              {autoPending && (
                <p className="text-sm text-muted" role="status">
                  {prep.status === "error"
                    ? "Experts are picked for your topic when you start."
                    : "Picking experts for your topic…"}
                </p>
              )}
              <div className="space-y-2.5 max-h-[380px] overflow-y-auto pr-1">
                {!autoPending && selectedExpertData.map((expert) => {
                  const reason = prepForTopic?.experts.find((e) => e.key === expert.value)?.reason;
                  const palette = getExpertPalette(expert.value, selectedExperts);
                  return (
                    <div
                      key={expert.value}
                      className="flex items-center gap-3 p-3 rounded-xl border border-line bg-subtle/50"
                    >
                      <div
                        className={`w-10 h-10 rounded-xl flex items-center justify-center text-xs font-semibold border flex-shrink-0 ${palette.badgeBg} ${palette.badgeText} ${palette.border}`}
                      >
                        {expert.label.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-xs font-semibold text-content block truncate">
                          {expert.label}
                        </span>
                        <span className="text-xs text-muted block line-clamp-2">
                          {reason || expert.description}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        {launchErrorNotice}
      </div>
    );
  }

  const stepSummaries = stepLabels.map((_, i) => {
    const s = i + 1;
    if (s === 1) return question.trim();
    if (s === 2) return `${plan.charAt(0).toUpperCase() + plan.slice(1)} tier`;
    if (s === 3) return debateMode === "multi" ? "Multi-expert" : "Deep analysis";
    if (s === TOTAL_STEPS) return "";
    if (s === 4) return autoPending ? "Picked for your topic" : `${selectedExperts.length} experts selected`;
    return `${audioMode ? "Audio" : "Text"} · ${concise ? "Concise" : "Detailed"}`;
  });

  return (
    <div className="wizard-workspace">
      <aside className="bubble-rail wizard-rail" aria-label="Debate setup">
        <div className="rail-heading"><span className="rail-icon"><Sparkles size={21} strokeWidth={1.6} /></span><h2>Debate setup</h2></div>
        <div className="wizard-stepper-track">
        <StepTab activeKey={`${step}-${stepLabels.length}`} selector=".step-bubble.is-current" />
        <ol className="wizard-stepper">
          {stepLabels.map((label, index) => {
            const s = index + 1;
            const current = s === step;
            return (
              <li key={label}>
                <button type="button" className={`step-bubble ${current ? "is-current" : ""} ${s < step ? "is-complete" : ""}`}
                  aria-current={current ? "step" : undefined} disabled={!canVisitStep(s)}
                  onClick={() => setStep(s)}>
                  <span className="step-number">{s < step ? <Check size={15} /> : String(s).padStart(2, "0")}</span>
                  <span className="step-text"><strong>{label}</strong>{stepSummaries[index] && <small>{stepSummaries[index]}</small>}</span>
                  {current && <span className="step-current-dot" aria-hidden="true" />}
                </button>
              </li>
            );
          })}
        </ol>
        </div>
        {(question.trim() || panel.length > 0) && <div className="rail-note council-blueprint">
          {question.trim() && <p className="line-clamp-3">{question}</p>}
          {debateMode === "multi" && panel.length > 0 && <div className="rail-expert-list">{panel.map(value => {
            const expert = expertOptions.find(e => e.value === value);
            const palette = getExpertPalette(value, panel);
            return expert ? <span key={value} className="motion-chip"><span aria-hidden="true" className={`mr-1 h-2 w-2 shrink-0 rounded-full border border-on-solid/40 ${palette.bar}`} />{expert.label}<button type="button" onClick={() => toggleExpert(value)} aria-label={`Remove ${expert.label}`}><X size={12} /></button></span> : null;
          })}</div>}
          {debateMode === "multi" && panel.length > 0 && <button type="button" onClick={() => { setPanelEdited(true); setPanelFor(topic); onClearExperts(); }}>Clear panel</button>}
        </div>}
        <div className="rail-bottom"><div className="flex items-center justify-between gap-2"><span>{isPrivileged ? "Free access on your account" : `${freeRemaining} of ${freeLimit} free debates left`}</span><span className="rail-status-dot" /></div><progress aria-label="Free debates remaining" max={100} value={freePct} />{showAudio && <span>{audioRemaining} audio debates left this week</span>}</div>
      </aside>

      <section className="wizard-canvas" aria-label="Council configuration">
        <nav className="compact-stepper" aria-label="Debate setup steps"><ol>{stepLabels.map((label, index) => {
          const s = index + 1;
          return <li key={label}><button type="button" disabled={!canVisitStep(s)} aria-current={s === step ? "step" : undefined} aria-label={`Step ${s}: ${label}`} onClick={() => setStep(s)}><span>{s < step ? <Check size={13} /> : s}</span><strong>{label}</strong></button></li>;
        })}</ol></nav>
        <div className="wizard-scroll" ref={canvasRef}>
          <div key={step} className="wizard-step-enter" data-direction={stepDirection}>
            {stepContent}
          </div>
        </div>
        <footer className="wizard-footer"><div className="wizard-footer-buttons">
          {navConfig.onBack && <button type="button" onClick={navConfig.onBack} className="button-secondary"><ArrowLeft size={16} /><span>Back</span></button>}
          {navConfig.secondary && <button type="button" onClick={navConfig.secondary.onClick} className="button-secondary"><SlidersHorizontal size={16} aria-hidden="true" /><span>{navConfig.secondary.label}</span></button>}
          <button type="button" disabled={navConfig.nextDisabled} onClick={navConfig.onNext} className="button-primary">{navConfig.nextIcon}<span>{navConfig.nextLabel}</span>{!navConfig.nextIcon && <ArrowRight size={16} />}</button>
        </div></footer>
      </section>
      <AnswerFormatDialog
        open={formatDialogOpen}
        onClose={() => setFormatDialogOpen(false)}
        format={answerFormat}
        detected={detectedFormat}
        onSave={(format) => {
          setFormatOverride({ topic, format });
          setFormatDialogOpen(false);
        }}
      />
    </div>
  );
};

export default DebateWizard;
